/**
 * VideoPreview - Modal for previewing and sharing recorded video
 *
 * Features:
 * - Customizable share button (render prop or default)
 * - Built-in Web Share API support with file sharing
 * - Works for both webar and webxr contexts
 */

import Button from "@core/components/Elements/Button/Button";
import emitter from '@webcore/core/events/eventemitter';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'preact/compat';

export interface VideoPreviewProps {
  /** Video URL to preview */
  videoUrl: string | null;
  /** Called when modal is closed */
  onClose: () => void;
  /** Custom share handler (if not provided, uses Web Share API) */
  onShare?: () => void;
  /** Title for share data (default: 'AR Experience') */
  shareTitle?: string;
  /** Text for share data */
  shareText?: string;
  /** Hide the share button entirely */
  hideShareButton?: boolean;
  /** Filename for shared file (default: 'ar-recording.mp4') */
  fileName?: string;
  /** Custom share button renderer */
  renderShareButton?: (handleShare: () => void) => ReactNode;
  /** Custom close button renderer */
  renderCloseButton?: (handleClose: () => void) => ReactNode;
  /** Custom buttons renderer */
  renderButtons?: (handlers: { share: () => void }) => ReactNode;
  /** Custom className for the overlay */
  className?: string;
  /** Initial aspect ratio hint before metadata probe completes */
  initialAspectRatio?: number | null;
}

export function VideoPreview({
  videoUrl,
  onClose,
  onShare,
  shareTitle = 'AR Experience',
  shareText = 'Check out this AR experience!',
  hideShareButton = false,
  fileName = 'ar-recording.mp4',
  renderShareButton,
  renderCloseButton,
  renderButtons,
  className = '',
  initialAspectRatio = null,
}: VideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showTapToUnmute, setShowTapToUnmute] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    if (!videoUrl) return;

    setVideoLoaded(false);
    setShowTapToUnmute(false);
    setVideoAspectRatio(initialAspectRatio ?? null);

    const probeVideo = document.createElement("video");
    probeVideo.preload = "metadata";
    probeVideo.src = videoUrl;
    probeVideo.onloadedmetadata = () => {
      const { videoWidth, videoHeight } = probeVideo;
      if (videoWidth > 0 && videoHeight > 0) {
        setVideoAspectRatio(videoWidth / videoHeight);
      }
    };
    probeVideo.onerror = () => {
      // Keep preview usable even if metadata probing fails.
      setVideoAspectRatio(9 / 16);
    };

    return () => {
      probeVideo.src = "";
      probeVideo.load();
    };
  }, [videoUrl, initialAspectRatio]);

  useEffect(() => {
    if (videoRef.current && videoUrl && videoAspectRatio) {
      const videoEl = videoRef.current;
      videoEl.src = videoUrl;
      // Start muted so autoplay is allowed even when recorded video has audio.
      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.load();
      videoEl.play().then(() => {
        setShowTapToUnmute(true);
      }).catch((err) => {
        if (err?.name === "NotAllowedError") {
          setShowTapToUnmute(true);
          return;
        }
        console.error("Error playing video:", err);
      });
    }
  }, [videoUrl, videoAspectRatio]);

  const handleTapToUnmute = useCallback((event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-preview-close="true"]')) {
      return;
    }

    const videoEl = videoRef.current;
    if (!videoEl || !showTapToUnmute) return;

    videoEl.muted = false;
    void videoEl.play()
      .then(() => setShowTapToUnmute(false))
      .catch((err) => {
        console.error("Error resuming preview with audio:", err);
      });
  }, [showTapToUnmute]);

  const handleClose = useCallback(() => {
    const videoEl = videoRef.current;
    if (videoEl) {
      // Stop audio immediately so close-fade cannot leak sound into next recording.
      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.pause();
    }
    setShowTapToUnmute(false);
    onClose();
  }, [onClose]);

  // Share handler - tries Web Share API first, falls back to emitter for iOS WebXR
  const handleShare = useCallback(async () => {
    if (onShare) {
      onShare();
      return;
    }

    if (!videoUrl) return;

    if (navigator.share) {
      try {
        const response = await fetch(videoUrl);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: 'video/mp4' });

        await navigator.share({
          title: shareTitle,
          text: shareText,
          files: [file],
        });
        return;
      } catch (fileShareError) {
        try {
          await navigator.share({
            title: shareTitle,
            text: shareText,
            url: videoUrl,
          });
          return;
        } catch (urlShareError) {
          // Both Web Share paths failed; fall through to the emitter.
        }
      }
    }

    emitter.emit('share', {
      title: shareTitle,
      text: shareText,
      url: videoUrl,
    });
  }, [videoUrl, shareTitle, shareText, fileName, onShare]);

  if (!videoUrl) return null;

  return (
    <div
      className={`absolute inset-0 z-[2000] bg-black/20 flex flex-col items-center justify-center p-4 ${className}`}
      style={{ pointerEvents: 'auto' }}
    >
      <div
        className="relative w-full max-w-[70vw] rounded-[22px] overflow-hidden border-[3px] border-white bg-black"
        onPointerDown={handleTapToUnmute}
        onTouchStart={handleTapToUnmute}
        style={{
          aspectRatio: videoAspectRatio ? `${videoAspectRatio}` : "9 / 16",
        }}
      >
        {!videoLoaded && (
          <div className="absolute inset-0 bg-black animate-pulse flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        <video
          ref={videoRef}
          className="w-full h-full object-cover block"
          playsInline
          autoPlay
          muted
          loop
          onLoadedData={() => setVideoLoaded(true)}
        />

        {renderCloseButton ? (
          <div
            data-preview-close="true"
            onPointerDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
          >
            {renderCloseButton(handleClose)}
          </div>
        ) : (
          <button
            onClick={handleClose}
            onTouchStart={handleClose}
            className="absolute top-4 right-4 z-10 group flex justify-center items-center hover:opacity-80"
            aria-label="Close video preview"
            data-preview-close="true"
            style={{ pointerEvents: "auto" }}
          >
            {/* Up state */}
            <svg
              width="54"
              height="54"
              viewBox="0 0 50 50"
              fill="none"
              className="block group-active:hidden"
            >
              <circle cx="25" cy="25" r="25" fill="black" fillOpacity="0.5" />
              <circle cx="25" cy="25" r="23.5" stroke="white" strokeWidth="2" />
              <path
                d="M22.9287 25.0055L16.4062 18.4789L18.4886 16.4062L25 22.9218L31.5224 16.4062L33.5937 18.4789L27.0823 25.0055L33.5937 31.5211L31.5224 33.5937L25 27.0782L18.4886 33.5937L16.4173 31.5211L22.9287 25.0055Z"
                fill="white"
              />
            </svg>
            {/* Down state (pressed) */}
            <svg
              width="54"
              height="54"
              viewBox="0 0 50 50"
              fill="none"
              className="hidden group-active:block"
            >
              <circle cx="25" cy="25" r="25" fill="white" />
              <circle cx="25" cy="25" r="23.5" stroke="white" strokeWidth="2" />
              <path
                d="M22.9287 25.0055L16.4062 18.4789L18.4886 16.4062L25 22.9218L31.5224 16.4062L33.5937 18.4789L27.0823 25.0055L33.5937 31.5211L31.5224 33.5937L25 27.0782L18.4886 33.5937L16.4173 31.5211L22.9287 25.0055Z"
                fill="black"
              />
            </svg>
          </button>
        )}
      </div>

      {renderButtons ? (
        renderButtons({ share: handleShare })
      ) : (
        <div className="flex flex-col gap-3 mt-6 w-full max-w-[260px] justify-center items-center">
          {!hideShareButton && (
            renderShareButton ? (
              renderShareButton(handleShare)
            ) : (
              <Button
                onClick={handleShare}
                className="!rounded-full font-medium text-lg backdrop-blur-sm !bg-transparent !border-2 !border-white !text-white active:!bg-white active:!text-black focus:!bg-white focus:!text-black md:hover:!bg-white md:hover:!text-black"
              >
                Share
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
}

export default VideoPreview;
